import { useMemo } from 'react';
import { useFormikContext } from 'formik';

import { ensureDefined } from '@e-socna/utils';
import { getDocumentName, getFragmentData } from '@e-socna/data-layer';
import type { MissionWorkflowId } from '@e-socna/data-layer';

import { MissionPage_OneMission_QueryDocument } from '../../../pages/Mission/hooks/useFindOneMissionQuery.hook';
import { useReferencesContext } from '../../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { ActorsCmp_Actor_Fragment } from '../../../components/Actors';

import { FolderBusinessEntityActorsFormStep } from '../../FolderBusinessEntity/steps/Actors';
import { useActorListsChangeFreezes } from '../../FolderBusinessEntity/steps/Actors/hooks/useActorListsChangeFreezes.hook';
import { useMissionAccess } from '../../../components/Access/hooks/useMissionAccess.hook';
import { quotationLockMessage } from '../../../components/Access/utils/quotation-lock-message.util';
import { missionKindProfileOf } from '../../../app/utils/mission-kind-profile.util';
import { useParentActorsNotice } from '../../../components/Actors/hooks/useParentActorsNotice.hook';
import { useParentActorsLevel } from '../../../components/Actors/hooks/useParentActorsLevel.hook';
import type { Props } from '../../FolderBusinessEntity/steps/Actors/types/props.type';

import { useCreateMissionActorMutation } from '../hooks/useCreateMissionActorMutation.hook';
import { useFlushMissionActorsMutation } from '../hooks/useFlushMissionActorsMutation.hook';
import { useCustomizeMissionActorsMutation } from '../hooks/useCustomizeMissionActorsMutation';
import { useRemoveMissionActorMutation } from '../hooks/useRemoveMissionActorMutation.hook';
import { useUpdateMissionActorMutation } from '../hooks/useUpdateMissionActorMutation.hook';
import { useIsFolderConfidential } from '../hooks/useIsFolderConfidential.hook';
import { useMissionCreationRefusal } from '../hooks/useMissionCreationRefusal.hook';
import type { MissionForm_Mission } from '../fragments/mission.fragment';

export const MissionActorsFormStep = ({
  fallbackActors,
  id,
  isInternal,
  paths,
  useDirectMutation,
}: Omit<
  Props,
  'isUnderConfidentiality' | 'mutations' | 'queryKeysToInvalidate' | 'rules'
>) => {
  const { values } = useFormikContext<MissionForm_Mission>();
  // The creation takes the roles held through the internal actors: their step tells a refusal.
  const creationRefusal = useMissionCreationRefusal(
    isInternal && !useDirectMutation,
  );
  const isFolderConfidential = useIsFolderConfidential();
  const previewFreezes = useActorListsChangeFreezes('mission', values._id);
  // An existing mission only: its edition writes each change at once.
  const { updateQuotationRelatedFields } = useMissionAccess(
    useDirectMutation ? values._id : undefined,
  );

  const { mutate: createActorMutation } = useCreateMissionActorMutation();
  const { mutate: flushActorsMutation } = useFlushMissionActorsMutation();
  const { mutate: customizeActorsMutation } =
    useCustomizeMissionActorsMutation();
  const { mutate: removeActorMutation } = useRemoveMissionActorMutation();
  const { mutate: updateActorMutation } = useUpdateMissionActorMutation();

  const mutations = {
    create: createActorMutation,
    flush: flushActorsMutation,
    customize: customizeActorsMutation,
    remove: removeActorMutation,
    update: updateActorMutation,
  };

  // The actors of each list are chosen among the same list of the parent level (the folder, or
  // the collaboration of a sub-mission).
  const parent = useParentActorsLevel({
    folderId: values.folderId,
    collaborationId: values.compositeMissionId,
  });
  const parentNotice = useParentActorsNotice({ isInternal, parent });
  const quotationLock = quotationLockMessage(
    updateQuotationRelatedFields,
    "son émetteur, son destinataire, leurs représentants légaux, l'interlocuteur du devis et les références des acteurs externes ne peuvent plus changer ; le reste des acteurs reste modifiable",
    missionKindProfileOf(values.kind).wording,
  );
  const notices = [
    ...(quotationLock ? [{ message: quotationLock }] : []),
    parentNotice,
  ];

  const selectionRestriction = useMemo(
    () => ({
      selectableIds: getFragmentData(
        ActorsCmp_Actor_Fragment,
        fallbackActors ?? [],
      ).map(({ thirdPartyId }) => thirdPartyId),
      reason: isInternal
        ? `Choisissez une organisation parmi les acteurs internes ${parent.ofLabel}.`
        : `Choisissez un tiers parmi les acteurs externes ${parent.ofLabel}.`,
    }),
    [fallbackActors, isInternal, parent.ofLabel],
  );

  const { actorCategoriesCodeMap, missionWorkflowsIdMap } =
    useReferencesContext();
  // A mission or a collaboration of the folder takes its quotation emitter among the emitters of
  // the folder, its receiver among its receivers; a sub-mission follows its collaboration.
  const quotationCategory = isInternal
    ? actorCategoriesCodeMap.QuotationEmitter
    : actorCategoriesCodeMap.QuotationReceiver;
  const categoryRestriction = useMemo(
    () =>
      values.compositeMissionId
        ? undefined
        : {
            categoryId: quotationCategory._id,
            holderIds: getFragmentData(
              ActorsCmp_Actor_Fragment,
              fallbackActors ?? [],
            )
              .filter(({ categoryIds }) =>
                categoryIds.includes(quotationCategory._id),
              )
              .map(({ thirdPartyId }) => thirdPartyId),
            notice: {
              message: isInternal
                ? "L'émetteur du devis se choisit parmi les émetteurs du devis du dossier. Pour en choisir un autre, donnez-lui d'abord ce rôle dans le dossier."
                : "Le destinataire du devis se choisit parmi les destinataires du devis du dossier. Pour en choisir un autre, donnez-lui d'abord ce rôle dans le dossier.",
              action: parentNotice.action,
            },
          },
    [
      fallbackActors,
      isInternal,
      parentNotice.action,
      quotationCategory._id,
      values.compositeMissionId,
    ],
  );
  const { workflow } = ensureDefined({
    workflow: missionWorkflowsIdMap[values.workflowId as MissionWorkflowId],
  });

  return (
    <FolderBusinessEntityActorsFormStep
      categoryRestriction={categoryRestriction}
      creationRefusal={creationRefusal}
      fallbackActors={fallbackActors}
      id={id}
      isInternal={isInternal}
      isUnderConfidentiality={values.isConfidential || isFolderConfidential}
      mutations={mutations}
      notices={notices}
      paths={paths}
      previewFreezes={useDirectMutation ? previewFreezes : undefined}
      queryKeysToInvalidate={
        [
          // The mission, and the sub-missions inheriting the lists of a
          // collaboration.
          [getDocumentName(MissionPage_OneMission_QueryDocument)],
        ] as readonly (readonly string[])[]
      }
      rules={workflow.settings.actorableRules}
      selectionRestriction={selectionRestriction}
      useDirectMutation={useDirectMutation}
    />
  );
};
